import { Select, type SelectProps, Spinner, Text } from '@lmn/design-system';
import { IconArrowDownLinear, IconUsers } from '@/app/common/icons/libraryIcons';
import type { HTMLAttributes, MouseEvent, ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { sorter } from '@/app/components/hotel-detail/common/common.helper';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import {
  type Category as CategoryType,
  type GoodToKnow,
  type MetaReview,
  type RatingByLanguage,
  type RatingByType,
  useMetaReview,
} from '@/app/components/hotel-detail/hooks/useMetaReview';
import {
  decorateRatingScore,
  getColor,
  getInterval,
  type RatingInterval,
  trackReviewUserInteraction,
  type UserInteraction,
} from '@/app/components/hotel-detail/trust-you/trustyou.helper';
import TrustYouScore from '@/app/components/hotel-detail/trust-you/TrustYouScore';
import { useTranslation } from '@/app/components/translations/Translation';
import { focusRing } from '@/app/components/search/styles/mixins.ts';
import { formatNumber } from '@/utils/numberUtils.ts';
import { Brand } from '@/domainLogic/index.ts';
import { Icon } from '@/app/common/icons/Icon';
import BasicModalHeader from '../../search/components/Modal/LayoutElements/BasicModalHeader';
import OpenXModal from '../../search/components/Modal/OpenXModal';

// A margin here, not on the icon: the icon's own margin holds its footprint.
const TravelTypeIcon = styled.span`
  display: inline-flex;
  margin-right: 8px;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createTravelTypeIcon = (active: boolean): Record<any, ReactElement> => {
  const color = active ? 'iconPrimaryDefault' : 'iconPrimary';

  return {
    all: <IconUsers />,
    family: (
      <TravelTypeIcon>
        <Icon category="People and users" name="icon-family" size={25} footprint={20} color={color} alt="families" />
      </TravelTypeIcon>
    ),
    couple: (
      <TravelTypeIcon>
        <Icon category="People and users" name="icon-couple" size={20} color={color} alt="couples" />
      </TravelTypeIcon>
    ),
    business: (
      <TravelTypeIcon>
        <Icon category="Services" name="icon-briefcase" size={20} color={color} alt="business" />
      </TravelTypeIcon>
    ),
    solo: (
      <TravelTypeIcon>
        <Icon category="People and users" name="icon-user" size={23} footprint={20} color={color} alt="solo" />
      </TravelTypeIcon>
    ),
  };
};
// #region Styled Components
const GroupScore = styled.span`
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};

  :not(:last-child) {
    margin-right: 4px;
  }
  :not(:first-child) {
    margin-left: 4px;
  }

  &::before {
    content: '∙';
    padding: 0 4px;
  }
`;

// Inline (Evo details modal) drops the card chrome: the surrounding tab section already supplies
// the page background and side padding, so the reviews content sits flush like the TripAdvisor one.
const TrustYouContainer = styled.div<{ $inline?: boolean }>`
  background-color: ${({ theme, $inline }) => ($inline ? 'transparent' : theme.kallisto.color.background.static.backgroundLayer02)};
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${({ $inline }) => ($inline ? '0' : '16px')};

  @media ${customCss.breakpoints.sm} {
    border-radius: ${({ $inline }) => ($inline ? '0' : '16px')};
    padding: ${({ $inline }) => ($inline ? '0' : '24px')};
    gap: 24px;
  }
`;

const Fallback = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const Header = styled.div`
  background: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${customCss.breakpoints.sm} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const SelectWrapper = styled.div`
  height: 44px;

  button {
    & > span {
      min-height: 44px;
    }

    [data-testid='selected-option-label'] {
      margin-top: 0;
      display: flex;
      align-items: center;
      height: 100%;
    }
  }
`;

const SelectButton = styled.button`
  height: 44px;
  width: 100%;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundNeutralSubtle};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.kallisto.color.border.interactive.borderNeutralDefault};
  }

  &:focus {
    ${focusRing};
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-height: 44px;
`;

const ChevronIcon = styled(IconArrowDownLinear)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconPrimary};
  flex-shrink: 0;
`;

const ModalOptionsList = styled.div`
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  border-radius: 12px;
  margin: 16px;
  overflow: hidden;
`;

const ModalOption = styled.button<{ $selected: boolean }>`
  width: 100%;
  border: none;
  background: none;
  padding: 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: transparent;

  // only applies hover effects on devices that actually support hover (like desktops with a mouse)
  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundNeutralHover};
    }
  }

  &:active {
    background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundNeutralHover};
  }
`;

const OptionContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Score = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const Categories = styled.div``;

const Name = styled.div`
  grid-area: name;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const ProgressBar = styled.div<{ $score: number; $color: string }>`
  grid-area: bar;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundDisabled};
  border-radius: 8px;
  height: 4px;
  margin: 0;
  position: relative;
  width: 100%;

  &::after {
    background-color: ${({ $color }) => $color};
    border-radius: 8px;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: ${({ $score }) => $score}%;
  }
`;

const Category = styled.div`
  display: grid;
  gap: 0 24px;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto 20px auto;
  grid-template-areas:
    'name reviews'
    'bar bar'
    'details details';
  border-top: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
  padding: 16px 0;

  ${ProgressBar} {
    align-self: center;
  }

  @media ${customCss.breakpoints.sm} {
    padding: 24px 0;
    grid-template-columns: 200px 1fr;
    grid-template-rows: auto 12px 1fr;
    grid-template-areas:
      'name details'
      'bar details'
      'reviews details';
  }
`;

const Quotes = styled.div`
  grid-area: details;
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

const Quote = styled(Text).attrs({ variant: 'Body' })`
  &::before {
    content: '"';
  }
  &::after {
    content: '"';
  }
`;

const BadgeSummary = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.kallisto.font.size['175']};
  background-color: ${({ $color, theme }) => $color || theme.kallisto.color.background.static.backgroundNeutralSubtle};
  border-radius: 8px;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  padding: 8px 12px;
  svg {
    margin-top: 2.4px;
    margin-right: 8px;
    flex-shrink: 0;
  }
`;

const Title = styled(Text).attrs({ variant: 'Title6' })``;

const Subtitle = styled(Text).attrs({ variant: 'BodySmall' })``;

const SpecialBadge = styled(BadgeSummary)`
  display: inline-grid;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    'icon title'
    'icon subtitle';
  align-items: center;
  column-gap: 8px;
  row-gap: 0;

  img {
    grid-area: icon;
    grid-row: span 2;
    height: 20px;
    width: 20px;
  }
  ${Title} {
    grid-area: title;
  }
  ${Subtitle} {
    grid-area: subtitle;
  }
`;

const GroupTitle = styled(Text).attrs({ variant: 'Title6' })`
  width: 100%;
`;

const GoodToKnows = styled.div`
  grid-area: good-to-know;
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
`;

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Tab = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  color: ${({ theme, $active }) => ($active ? theme.kallisto.color.text.interactive.textPrimaryDefault : theme.kallisto.color.text.static.textPrimary)};
  border-bottom: 2px solid
    ${({ theme, $active }) => ($active ? theme.kallisto.color.border.interactive.borderPrimaryDefault : 'transparent')};
  font-size: ${({ theme }) => theme.kallisto.font.size['175']};
  font-weight: ${(p) => p.theme.kallisto.font.weight.bold};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: none;
    border: 2px solid ${({ theme }) => theme.kallisto.color.border.interactive.borderNeutralPressed};
    border-bottom: 2px solid
      ${({ theme, $active }) => ($active ? theme.kallisto.color.border.interactive.borderPrimaryDefault : 'transparent')};
    padding: 4px 14px 6px;
  }

  ${GroupScore} {
    color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
    font-weight: ${({ theme }) => theme.kallisto.font.weight.regular};
  }
  svg,
  [data-library-icon] {
    height: 24px;
    width: 24px;
    margin-right: 8px;
  }
`;

const Tabs = styled.div<{ $inline?: boolean }>`
  display: flex;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  margin: 0 -16px;
  padding: 0 16px;
  border-bottom: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};

  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }

  @media ${customCss.breakpoints.sm} {
    margin: 0 -24px;
    padding: 0 24px;
  }

  /* Inline: the container has no padding to bleed into, so keep the underline aligned to the
     content edge instead of stretching it past the tab's side inset. */
  ${({ $inline }) =>
    $inline &&
    css`
      margin: 0;
      padding: 0;

      @media ${customCss.breakpoints.sm} {
        margin: 0;
        padding: 0;
      }
    `}
`;

const Ranking = styled.div`
  grid-area: ranking;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
`;

const Highlights = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  border-radius: 8px;

  ${BadgeSummary} {
    width: fit-content;
  }
`;

const TypeSummary = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.kallisto.color.background.static.backgroundNeutralSubtle};
`;

// #endregion

type TravelerType = 'all' | 'family' | 'couple' | 'business' | 'solo';

type GoodToKnowItem = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: ReactElement<any> | null;
  color: string;
  text: string;
};

type TypeRatingItem = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: (active: boolean) => ReactElement<any>;
  percentage: string;
  type: TravelerType;
  text: string;
};

const trackTypes: Dynamic<UserInteraction> = {
  TabClick: {
    action: 'tab',
    label: 'all',
    category: 'all_reviews_modal',
  },
};

type TrackType = keyof typeof trackTypes;

function trackUserInteraction(type: TrackType, label?: string) {
  trackReviewUserInteraction(trackTypes, type, label);
}

export type TrustYouReviewsModalContentProps = {
  language: string;
  trustYouHotelId: string;
  preselectedType?: TravelerType;
  /** Flush rendering inside the Evo details modal reviews tab (no card background/padding). */
  inline?: boolean;
} & HTMLAttributes<HTMLElement>;

export default function TrustYouReviewsModalContent(props: TrustYouReviewsModalContentProps) {
  const { language, trustYouHotelId, preselectedType, inline = false, ...rest } = props;
  const { localization } = usePageWrapperData();
  const { metaReview, isLoading } = useMetaReview(trustYouHotelId, language);
  const { t } = useTranslation();
  const theme = useTheme();
  const [reviewsLanguage, setReviewsLanguage] = useState('all');
  const [reviewsType, setReviewsType] = useState<TravelerType>(preselectedType || 'all');
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const tabsRef = useRef<Map<TravelerType, HTMLElement>>(new Map());
  const ratingInterval = useMemo(
    (): RatingInterval | undefined => (metaReview ? getInterval(metaReview.score) : undefined),
    [metaReview]
  );
  const { locale } = localization;

  const languages = useMemo(
    (): SelectProps['options'] =>
      metaReview
        ? [
            {
              value: 'all',
              label: (
                <>
                  <Text variant="Body">{t('vacation.select.details.review.language.all')}</Text>
                  <GroupScore>100%</GroupScore>
                </>
              ),
            },
            ...metaReview.ratingByLanguage
              .sort((rating1: RatingByLanguage, rating2: RatingByLanguage): number =>
                sorter(rating1, rating2, (rating: RatingByLanguage): number => rating.percentage, true)
              )
              .map((rating: RatingByLanguage) => ({
                value: rating.language,
                label: (
                  <>
                    <Text variant="Body">{t(`vacation.select.details.review.language.${rating.language}`)}</Text>
                    <GroupScore>{rating.percentage}%</GroupScore>
                  </>
                ),
              })),
          ]
        : [],
    [metaReview, t]
  );

  const review = useMemo(
    (): MetaReview | RatingByLanguage | undefined =>
      reviewsLanguage === 'all'
        ? metaReview
        : metaReview
          ? metaReview.ratingByLanguage.find((rating: RatingByLanguage): boolean => rating.language === reviewsLanguage)
          : undefined,
    [metaReview, reviewsLanguage]
  );

  const typeReview = useMemo(
    (): MetaReview | RatingByLanguage | RatingByType | undefined =>
      reviewsType === 'all'
        ? review
        : review?.ratingByTypeList.find((typeRating: RatingByType): boolean => typeRating.type === reviewsType),
    [review, reviewsType]
  );

  const categories = useMemo((): CategoryType[] => {
    let list: CategoryType[];
    if (!typeReview) {
      list = [];
    } else if (reviewsLanguage === 'all' && reviewsType === 'all') {
      list = (typeReview as MetaReview).allCategories;
    } else {
      list = typeReview.categories;
    }

    return list;
  }, [typeReview, reviewsLanguage, reviewsType]);

  const typeRatingList = useMemo(
    (): TypeRatingItem[] =>
      review
        ? [
            {
              icon: (active: boolean) => createTravelTypeIcon(active).all,
              percentage: '100%',
              type: 'all',
              text: t('vacation.common.rating.type.all'),
            },
            ...review.ratingByTypeList.map((rating: RatingByType): TypeRatingItem => ({
              icon: (active: boolean) => createTravelTypeIcon(active)[rating.type],
              percentage: `${rating.percentage}%`,
              type: rating.type as TravelerType,
              text: t(`vacation.common.rating.type.${rating.type}`),
            })),
          ]
        : [],
    [review]
  );

  const goodToKnows = useMemo((): GoodToKnowItem[] => {
    const list: GoodToKnow[] = typeReview?.goodToKnowList || [];

    return list.map((goodToKnow: GoodToKnow): GoodToKnowItem =>
      goodToKnow.sentiment === 'pos'
        ? {
            icon: (
              <Icon category="Rating and feedback" name="icon-thumb-up" size={14} color="iconPrimary" alt="Like icon" />
            ),
            color: theme.kallisto.color.background.static.backgroundSuccessSubtle,
            text: goodToKnow.text,
          }
        : goodToKnow.sentiment === 'neg'
          ? {
              icon: (
                <Icon
                  category="Rating and feedback"
                  name="icon-thumb-down"
                  size={14}
                  color="iconPrimary"
                  alt="Dislike icon"
                />
              ),
              color: theme.kallisto.color.background.static.backgroundWarningSubtle,
              text: goodToKnow.text,
            }
          : {
              icon: null,
              color: '',
              text: goodToKnow.text,
            }
    );
  }, [typeReview, reviewsType, theme]);

  const badges = useMemo(() => (metaReview ? metaReview.badges : []), [metaReview]);

  const tabClickHandler = useCallback(
    (event: MouseEvent): void => {
      const tab: HTMLElement | null = (event.target as HTMLElement).closest('[data-id]');
      if (tab?.dataset.id) {
        setReviewsType(tab.dataset.id as TravelerType);
        trackUserInteraction('TabClick', tab.dataset.id);
      }
    },
    [setReviewsType]
  );

  const tabKeyDownHandler = useCallback(
    (event: React.KeyboardEvent): void => {
      const tab: HTMLElement | null = event.currentTarget as HTMLElement;
      if (!tab?.dataset.id) return;

      const currentIndex = typeRatingList.findIndex((rating) => rating.type === tab.dataset.id);
      let nextIndex = currentIndex;
      let shouldNavigate = false;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        nextIndex = (currentIndex + 1) % typeRatingList.length;
        shouldNavigate = true;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        nextIndex = (currentIndex - 1 + typeRatingList.length) % typeRatingList.length;
        shouldNavigate = true;
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setReviewsType(tab.dataset.id as TravelerType);
        trackUserInteraction('TabClick', tab.dataset.id);
        return;
      } else if (event.key === 'Home') {
        event.preventDefault();
        nextIndex = 0;
        shouldNavigate = true;
      } else if (event.key === 'End') {
        event.preventDefault();
        nextIndex = typeRatingList.length - 1;
        shouldNavigate = true;
      }

      if (shouldNavigate && nextIndex !== currentIndex) {
        const nextTab = typeRatingList[nextIndex];
        if (nextTab) {
          const nextElement = tabsRef.current.get(nextTab.type as TravelerType);
          if (nextElement) {
            setReviewsType(nextTab.type as TravelerType);
            trackUserInteraction('TabClick', nextTab.type);
            nextElement.focus();
          }
        }
      }
    },
    [typeRatingList, setReviewsType]
  );

  const openLanguageModal = useCallback(() => {
    setLanguageModalOpen(true);
  }, []);

  const closeLanguageModal = useCallback(() => {
    setLanguageModalOpen(false);
  }, []);

  const handleLanguageSelect = useCallback((languageSet: string) => {
    setReviewsLanguage(languageSet);
    setLanguageModalOpen(false);
  }, []);

  const selectedLanguageOption = useMemo(
    () => languages.find((lang) => lang.value === reviewsLanguage),
    [languages, reviewsLanguage]
  );

  useEffect((): void => {
    if (!typeRatingList.some((typeRating: TypeRatingItem): boolean => typeRating.type === reviewsType)) {
      setReviewsType('all');
    }
  }, [typeRatingList, reviewsType, setReviewsType]);

  useEffect((): void => {
    if (preselectedType) {
      setReviewsType(preselectedType);
    }
  }, [preselectedType, setReviewsType]);

  // Mirror the TripAdvisor modal: spinner while loading, a graceful message when the
  // meta-review fetch fails or returns nothing — instead of a blank modal.
  if (!metaReview) {
    return (
      <TrustYouContainer {...rest} $inline={inline} data-nosnippet={true}>
        <Fallback>
          {isLoading ? (
            <Spinner size={32} variant="accent" />
          ) : (
            <Text variant="Body" color="textSecondary">
              {t('vacation.select.details.reviews.error') || 'Reviews are currently unavailable.'}
            </Text>
          )}
        </Fallback>
      </TrustYouContainer>
    );
  }

  const { reviewCount, score } = metaReview;

  return (
    <TrustYouContainer {...rest} $inline={inline} data-nosnippet={true}>
      <Header>
        <Score>
          {score ? (
            <TrustYouScore
              score={score}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                ${(p) => p.theme.kallisto.mixins.font('Title4')};
                padding: 8px;
                height: 40px;
                width: 40px;

                @media ${customCss.breakpoints.sm} {
                  height: 52px;
                  width: 52px;
                  ${(p) => p.theme.kallisto.mixins.font('Title2')};
                }
              `}
            />
          ) : null}
          <div
            css={`
              display: flex;
              flex-direction: column;
              @media ${customCss.breakpoints.sm} {
                flex-direction: row;
                align-items: baseline;
                gap: 4px;
              }
            `}
          >
            <Text
              variant="Title5"
              css={css`
                @media ${customCss.breakpoints.sm} {
                  ${(p) => p.theme.kallisto.mixins.font('Title4')};
                }
              `}
            >
              {ratingInterval && t(ratingInterval.translationKey)}
            </Text>
            <Text
              variant="Caption"
              color="textSecondary"
              css={css`
                @media ${customCss.breakpoints.sm} {
                  ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
                  &::before {
                    content: '·';
                    margin-right: 4px;
                  }
                }
              `}
            >
              {t('vacation.select.details.review.scoredetails', {
                reviewCount: formatNumber({ locale, number: reviewCount }),
              })}
            </Text>
          </div>
        </Score>
        <Filters>
          {isMobile ? (
            <SelectButton onClick={openLanguageModal} aria-label={t('vacation.select.details.review.language')}>
              <ButtonContent>{selectedLanguageOption?.label}</ButtonContent>
              <ChevronIcon />
            </SelectButton>
          ) : (
            <SelectWrapper>
              <Select
                label=""
                aria-label={t('vacation.select.details.review.language')}
                enableItemSearch={false}
                options={languages}
                value={reviewsLanguage}
                onChange={setReviewsLanguage}
              />
            </SelectWrapper>
          )}
        </Filters>
      </Header>
      <Tabs onClick={tabClickHandler} role="tablist" $inline={inline}>
        {typeRatingList.map((rating) => (
          <Tab
            key={rating.type}
            ref={(el) => {
              if (el) {
                tabsRef.current.set(rating.type as TravelerType, el);
              } else {
                tabsRef.current.delete(rating.type as TravelerType);
              }
            }}
            data-id={rating.type}
            $active={rating.type === reviewsType}
            role="tab"
            aria-selected={rating.type === reviewsType}
            aria-controls={`reviews-panel-${rating.type}`}
            tabIndex={rating.type === reviewsType ? 0 : -1}
            onKeyDown={tabKeyDownHandler}
          >
            {rating.icon(rating.type === reviewsType)} {rating.text}
            <GroupScore aria-hidden="true">{rating.percentage}</GroupScore>
          </Tab>
        ))}
      </Tabs>

      <div
        role="tabpanel"
        id={`reviews-panel-${reviewsType}`}
        aria-labelledby={`tab-${reviewsType}`}
        css={`
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        {goodToKnows?.length || badges?.length || typeReview?.summaryText ? (
          <Highlights>
            {typeReview?.summaryText && (
              <TypeSummary>
                <Text
                  variant="Body"
                  dangerouslySetInnerHTML={{
                    __html: t('vacation.select.details.review.type.summary', {
                      reviewScore:
                        decorateRatingScore(typeReview?.score ? Number(typeReview?.score) : undefined) || '-',
                      travelerType: t(`vacation.common.rating.type.${reviewsType}.summary.placeholder`),
                    }).replace('100', '10'),
                  }}
                />
                <Quote>{typeReview?.summaryText}</Quote>
              </TypeSummary>
            )}
            {goodToKnows?.length ? (
              <GoodToKnows>
                <GroupTitle>{t('vacation.select.details.review.goodtoknow')}</GroupTitle>
                {goodToKnows.map((goodToKnow) => (
                  <BadgeSummary key={goodToKnow.text} $color={goodToKnow.color}>
                    {goodToKnow.icon}
                    <TextBase>{goodToKnow.text}</TextBase>
                  </BadgeSummary>
                ))}
              </GoodToKnows>
            ) : null}

            {badges?.length ? (
              <Ranking>
                <GroupTitle>{t('vacation.select.details.review.badges')}</GroupTitle>
                {badges.map((badge) => (
                  <SpecialBadge key={badge.text}>
                    <Icon category="Financial" name="icon-discount" size={20} color="iconPrimary" alt={badge.text} />
                    <Title>{badge.text}</Title>
                    <Subtitle>{badge.subtext}</Subtitle>
                  </SpecialBadge>
                ))}
              </Ranking>
            ) : null}
          </Highlights>
        ) : null}
        <Categories>
          {categories.map((category: CategoryType) => (
            <Category key={category.name}>
              <Name>
                <Text variant="Title5">{decorateRatingScore(category.score)}</Text>
                <Text
                  variant="Body"
                  css={`
                    flex-grow: 1;
                  `}
                >
                  {category.name}
                </Text>
              </Name>
              <Text
                variant="BodySmall"
                css={`
                  grid-area: reviews;
                  display: flex;
                  align-items: flex-end;
                  justify-content: flex-end;

                  @media ${customCss.breakpoints.sm} {
                    justify-content: flex-start;
                    align-items: flex-start;
                  }
                `}
              >
                {t('vacation.select.details.review.scoredetails.short', {
                  reviewCount: formatNumber({ locale, number: category.count }),
                })}
              </Text>
              <ProgressBar $score={category.score} $color={getColor(category.score)} />
              <Quotes>
                {category.summaryText ? <Quote>{category.summaryText}</Quote> : null}
                {category.summarySubtext ? <Text variant="BodySmall">{category.summarySubtext}</Text> : null}
              </Quotes>
            </Category>
          ))}
        </Categories>
      </div>

      {isMobile && (
        <OpenXModal
          isOpen={languageModalOpen}
          isMobile={true}
          onClose={closeLanguageModal}
          onCloseByBrowserHistory={closeLanguageModal}
          onOpenByBrowserHistory={openLanguageModal}
          fullScreen={false}
          brand={Brand.LASTMINUTE}
          id="language-selection-modal"
          aria-label={t('vacation.select.details.review.language')}
          bodyBackgroundColor="backgroundLayer01"
          bodyPadding={0}
          bodyPaddingS={0}
          bodyPaddingXS={0}
          header={{
            sticky: true,
            element: (
              <BasicModalHeader handleClose={closeLanguageModal}>
                {t('vacation.select.details.review.language')}
              </BasicModalHeader>
            ),
          }}
        >
          <ModalOptionsList>
            {languages.map((option) => (
              <ModalOption
                key={option.value}
                $selected={option.value === reviewsLanguage}
                onClick={() => handleLanguageSelect(option.value)}
              >
                <OptionContent>
                  <div>{option.label}</div>
                  {option.value === reviewsLanguage && (
                    <Icon
                      category="Semantics"
                      name="icon-check"
                      size={20}
                      color="iconPrimary"
                      alt="selected-language"
                    />
                  )}
                </OptionContent>
              </ModalOption>
            ))}
          </ModalOptionsList>
        </OpenXModal>
      )}
    </TrustYouContainer>
  );
}
